/**
 * StepViewFrame — the shared chrome every step view renders inside.
 *
 * Story 26739. Visual contract: `tasks/design/step-views.html` at 310px. Every
 * pane in that file opens the same way — a pill eyebrow saying what ora wants
 * ("Needs You" / "Running" / "Pending Email Send" / "Done"), then the run's
 * identity as a heading ("Delgado · HP-4471822"), then the view's own content.
 *
 * WHY A FRAME AND NOT SIX LAYOUTS. The six views are the same pane at six
 * moments. If each owned its own spacing and heading levels, the pane would
 * appear to JUMP between moments — and a jump reads as "something else
 * happened", which is exactly the anxiety this surface exists to remove. So
 * position and rhythm are fixed here; only the words and the body change.
 *
 * `data-step-view` / `data-testid="step-view-<name>"` are the handles both
 * hosts' tests select on. They are part of the contract: a host test asserting
 * "the exception view is showing" must not have to match on copy.
 *
 * No Office.js, no Remix server import (design Constraint 20).
 */
import type { ReactNode } from "react";
import { statusPillClass, type StatusPillTone } from "../statusPill";
import type { StepViewName } from "./stepViewFor";

/**
 * The eyebrow's weight.
 *
 * `attention` is the amber "Needs You" status pill (ora's Monitor badge,
 * `statusPill.ts`) whenever ora is waiting on the Servicer; `quiet` is the
 * same pill in calm colours for a state that needs nothing from them;
 * `warning` is the amber "not confirmed" look for a Done view that cannot claim
 * success. Accent only — the words say it too, so the pill is never the sole carrier of the
 * meaning.
 *
 * It was a filled sunrise capsule with a black outline until 2026-10-05: the
 * Send button's recipe, so the status read as something to click.
 */
export type StepViewTone = "attention" | "quiet" | "warning";

const EYEBROW_TONE: Record<StepViewTone, StatusPillTone> = {
  attention: "needsYou",
  quiet: "neutral",
  warning: "warning",
};

export interface StepViewFrameProps {
  /** Which of the six this is. Drives the test handles, nothing visual. */
  view: StepViewName;
  /** The pill: "Needs You", "Running", "Pending Email Send", "Done". */
  eyebrow?: ReactNode;
  tone?: StepViewTone;
  /** The run's identity — "<client> · <policy>". */
  headline?: ReactNode;
  /**
   * One calm line under the heading. On the Running view this is the section
   * sentence from `sectionLabelFor` — never a number (design decision D4).
   */
  subline?: ReactNode;
  /** The view's own content. */
  children?: ReactNode;
  /** Rendered last, below everything. */
  footer?: ReactNode;
  /** Optional marker for tests/hosts, e.g. the Done view's outcome. */
  dataOutcome?: string;
}

/**
 * "<client> · <policy>", skipping whichever half is missing. Returns null when
 * neither is known, so the heading disappears rather than rendering a lone
 * separator.
 */
export function identityLine(
  clientName?: string | null,
  policyNumber?: string | null,
): string | null {
  const parts = [clientName, policyNumber]
    .map((p) => (typeof p === "string" ? p.trim() : ""))
    .filter((p) => p !== "");
  return parts.length > 0 ? parts.join(" · ") : null;
}

export function StepViewFrame({
  view,
  eyebrow,
  tone = "attention",
  headline,
  subline,
  children,
  footer,
  dataOutcome,
}: StepViewFrameProps) {
  return (
    <div
      className="flex flex-col gap-2.5"
      data-testid={`step-view-${view}`}
      data-step-view={view}
      data-outcome={dataOutcome}
    >
      {eyebrow ? (
        <span
          className={`self-start ${statusPillClass(EYEBROW_TONE[tone])}`}
          data-testid="step-view-eyebrow"
        >
          {eyebrow}
        </span>
      ) : null}

      {headline ? (
        <p
          className="text-[15px] font-semibold leading-tight text-primary-gray-light-900"
          data-testid="step-view-headline"
        >
          {headline}
        </p>
      ) : null}

      {subline ? (
        <p
          className="text-xs leading-relaxed text-primary-gray-light-800"
          data-testid="step-view-subline"
        >
          {subline}
        </p>
      ) : null}

      {children}

      {footer ?? null}
    </div>
  );
}

export default StepViewFrame;
